// Locale machinery — see /i18n-plan.md for the full plan.
//
// English is unprefixed and lives at the site's existing URLs (non-negotiable,
// see plan §3.1): middleware.ts rewrites unprefixed requests to `/${defaultLocale}`
// internally so `app/[locale]/page.tsx` can serve them without the browser ever
// seeing an `/en` prefix. Additional locales get a real `/xx/` prefix.
export const locales = ['en', 'es', 'pt-BR', 'zh-CN', 'ja', 'fr', 'ar'] as const
export type Locale = (typeof locales)[number]
export const defaultLocale: Locale = 'en'

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value)
}

const baseUrl = 'https://seedeletedtweets.com'

// Prefixes `path` with the locale, except for the default locale which stays
// unprefixed. Use this for any internal link so it keeps working once a
// second locale exists.
export function localeHref(locale: Locale, path: string = '/'): string {
  if (locale === defaultLocale) return path
  return path === '/' ? `/${locale}` : `/${locale}${path}`
}

// og:locale expects an underscore-separated tag from Facebook's supported
// list, not a bare BCP-47 locale - see the Open Graph locale reference.
// es_LA (Latin America) matches the neutral Spanish used in messages/es.ts.
const ogLocales: Record<Locale, string> = {
  en: 'en_US',
  es: 'es_LA',
  'pt-BR': 'pt_BR',
  'zh-CN': 'zh_CN',
  ja: 'ja_JP',
  fr: 'fr_FR',
  // ar_AR is Facebook's generic Arabic entry (there's no ar_SA on its list).
  ar: 'ar_AR',
}

export function ogLocale(locale: Locale): string {
  return ogLocales[locale]
}

// Text direction for <html dir>. Pages whose UI isn't translated (the
// package viewer, /download) pin themselves back to ltr, since mirroring an
// English interface inside an Arabic page only makes it harder to read.
const rtlLocales: readonly Locale[] = ['ar']

export function localeDir(locale: Locale): 'rtl' | 'ltr' {
  return rtlLocales.includes(locale) ? 'rtl' : 'ltr'
}

// hreflang alternates for a given canonical path, reciprocal across
// `availableLocales` (default: every shipped locale) plus x-default ->
// English. Pass the requesting page's own `locale` (its canonical must
// self-reference, not always point at English - a page whose canonical
// names a different URL tells Google to consolidate ranking signals onto
// that other URL instead, which silently kills indexing/ranking for every
// non-English locale) and the unprefixed (English) path, e.g. '/faq' or '/'
// for the homepage. Pages that aren't translated into every locale (e.g.
// the blog - see i18n-plan.md §3.6) should pass an explicit, narrower list
// so this doesn't claim a translated version that doesn't exist.
export function localeAlternates(
  locale: Locale,
  path: string = '/',
  availableLocales: readonly Locale[] = locales
) {
  const languages: Record<string, string> = {}
  for (const l of availableLocales) {
    languages[l] = `${baseUrl}${localeHref(l, path)}`
  }
  languages['x-default'] = `${baseUrl}${path}`
  return {
    canonical: `${baseUrl}${localeHref(locale, path)}`,
    languages,
  }
}
